// M2 logging, coring & geophysics (M2_SPEC §12): torsion balance, refraction and reflection seismic (perField, with
// salt-dome sigma and pay-depth estimates), the Schlumberger electric log and the rotary core barrel (perWell).
import { normal } from '../../core/rng.js';
import { eqDef, fieldDef, stat, unitPrice, isAvailable, rigStats } from '../defs.js';
import { strataAt, reservoirsAt } from '../geo.js';
import { hasUpgrade, effects } from '../research.js';
import { notify, spend, fmt$ } from '../util.js';

export const GEOPHYSICAL = new Set(['torsion_balance', 'seismic_refraction', 'seismic_2d', 'seismic_3d', 'seismic_4d_ai']);
export const CORE_FT = 60;

// A Gulf Coast-style piercement dome: the field's column has a salt core.
export const isDomeField = (fdef) => (fdef?.strata ?? []).some((s) => s.rock === 'salt');

export function hasGeophysicist(state) {
  return Object.values(state.crew).some((c) => c.role === 'geophysicist' && c.status === 'active' && !c.injuredUntil);
}

// Price multiplier for a survey method (in-house geophysical crew halves seismic & gravity work).
export function surveyPriceMult(state, def) {
  return GEOPHYSICAL.has(def?.id) && hasGeophysicist(state) ? 0.5 : 1;
}

// Effective sigma for a plot from a survey def on this field (before survey.js's lucky/geologist tweaks).
export function surveySigma(state, def, fdef, seep = false) {
  let sigma = stat(def, 'sigma', 0.45);
  if (seep) sigma = stat(def, 'seepSigma', sigma);
  const dome = stat(def, 'domeSigma', null);
  if (dome != null && isDomeField(fdef)) sigma = Math.min(sigma, dome);
  return sigma;
}

// Geologist sigma bonus from upgrades (anticline theory, micropaleontology).
export function geologistSigmaDelta(state) {
  return effects(state).geoSigmaDelta ?? 0;
}

// Structure depth at a plot from the nearest prospect's shallowest reservoir (seismic maps structure, not oil, so
// dry plots get an estimate too).
export function structureDepthAt(fdef, plot) {
  const rs = reservoirsAt(fdef, plot);
  if (rs.length) return rs[0].top;
  let best = null;
  for (const p of fdef.prospects ?? []) {
    const r = (p.reservoirs ?? []).slice().sort((a, b) => a.top - b.top)[0];
    if (!r) continue;
    const dist = Math.hypot(plot.x - p.cx, plot.y - p.cy);
    const top = (r.top + (r.dipFtPerPlot ?? 0) * dist) * (plot.truth?.depthMult ?? 1);
    if (!best || dist < best.dist) best = { dist, top };
  }
  return best ? Math.round(best.top * 10) / 10 : null;
}

// After a perField survey with depthErr: estimated pay/structure depth per plot (normal error, σ = depthErr/3,
// so ~99.7% of plots land within ±depthErr; tails clamped at 1.5×). Keeps the better estimate when surveyed again.
export function applyDepthEstimates(state, fieldId, def) {
  const err = stat(def, 'depthErr', null);
  if (!err) return;
  const fdef = fieldDef(fieldId);
  for (const p of state.fields[fieldId].plots) {
    const truth = structureDepthAt(fdef, p);
    if (truth == null) continue;
    if (p.survey?.payDepth && p.survey.payDepth.err <= err) continue;
    const est = Math.max(10, truth * (1 + Math.max(-1.5 * err, Math.min(1.5 * err, normal(state, 0, err / 3)))));
    p.survey = p.survey ?? { est: p.pubEst ?? 0.3, sigma: 0.5, methods: [] };
    p.survey.payDepth = { est: Math.round(est), err, method: def.id };
  }
}

// ---- electric log ---------------------------------------------------------------------------
const LOGGABLE = new Set(['DRILLING', 'PAUSED', 'AT_TD', 'DRY', 'SUSPENDED', 'PRODUCING', 'SHUT_IN', 'DEPLETED', 'TRIPPING', 'COMPLETING']);

export function canLog(state, well) {
  const def = eqDef('e_log_run');
  if (!well) return 'No such well.';
  if (well.ownerOperatorId) return `${well.name} belongs to the operator.`;
  if (!def || !isAvailable(def, state)) return 'Schlumberger’s electric logging arrives in 1927.';
  const up = stat(def, 'requiresUpgrade', 'e_logs');
  if (up && !hasUpgrade(state, up)) return 'Needs Electric Logging (research or 1927).';
  if (!(well.depth > 0)) return 'Nothing drilled to log yet.';
  if (!LOGGABLE.has(well.status)) return `Can't log a well that is ${String(well.status).toLowerCase().replace(/_/g, ' ')}.`;
  return null;
}

export function runElogImpl(state, wellId) {
  const well = state.wells[wellId];
  const why = canLog(state, well);
  if (why) return { ok: false, msg: why };
  const def = eqDef('e_log_run');
  const price = unitPrice(state, def);
  if (!spend(state, price, 'consumables')) return { ok: false, msg: `A log run costs ${fmt$(price)}.` };
  well.cost = (well.cost ?? 0) + price;
  const fdef = fieldDef(well.fieldId);
  const plot = state.fields[well.fieldId].plots[well.plot];
  const res = reservoirsAt(fdef, plot).filter((r) => r.top < well.depth);
  const zones = strataAt(fdef, plot).filter((s) => s.top < well.depth).map((s) => {
    const r = res.find((x) => x.top >= s.top && x.top < s.bottom);
    return { top: s.top, bottom: Math.min(s.bottom, well.depth), rock: s.rock, name: s.name, pore: r ? r.pore ?? s.pore : s.pore, frac: s.frac,
      water: !!s.water, gas: !!s.gas, oil: !!r, resId: r?.id ?? null, resTop: r?.top ?? null, resThick: r?.thick ?? null };
  });
  well.eLog = { day: state.day, depth: well.depth, zones };
  well.eLogged = true;
  const found = revealReservoirs(state, well, res.map((r) => r.id), 'electric log');
  const maxPore = Math.max(0, ...zones.map((z) => z.pore ?? 0));
  well.knownPore = Math.max(well.knownPore ?? 0, maxPore);
  const f = state.fields[well.fieldId];
  f.poreLog ??= [];
  for (const z of zones) if (z.pore > 8.8 || z.oil) f.poreLog.push({ top: z.top, bottom: z.bottom, pore: z.pore, resId: z.resId, wellId, day: state.day });
  if (f.poreLog.length > 200) f.poreLog.splice(0, f.poreLog.length - 200);
  const msg = found.length
    ? `Electric log on ${well.name}: pay the drillers missed! ${found.length} zone${found.length > 1 ? 's' : ''} behind the bit — recomplete up-hole.`
    : `Electric log on ${well.name}: ${zones.length} zones read, ${res.length ? `${res.length} oil zone${res.length > 1 ? 's' : ''}` : 'no oil-bearing sand'}.`;
  notify(state, 'news', { kind: 'survey', wellId, fieldId: well.fieldId, text: msg, level: found.length ? 'major' : 'info' });
  return { ok: true, msg };
}

// Reveal reservoirs on a well; returns the ids that were bypassed (missed pay) and are now found.
export function revealReservoirs(state, well, ids, how) {
  well.revealed ??= [];
  const found = [];
  for (const id of ids) {
    if (!well.revealed.includes(id)) well.revealed.push(id);
    if (well.bypassed?.includes(id)) {
      well.bypassed = well.bypassed.filter((x) => x !== id);
      (well.bypassedFound ??= []).push({ resId: id, day: state.day, how });
      found.push(id);
    }
  }
  return found;
}

// ---- core barrel ---------------------------------------------------------------------------
export function canCore(state, well) {
  const def = eqDef('rotary_coring');
  if (!well) return 'No such well.';
  if (!def || !isAvailable(def, state)) return 'Core barrels arrive in 1921.';
  const rig = well.rigId ? state.rigs[well.rigId] : null;
  const rotary = (well.rigType ?? (rig ? rigStats(eqDef(rig.defId)).rigType : null)) === 'rotary' || !!well.contract;
  if (!rotary) return 'Coring needs a rotary rig on the well.';
  if (!['DRILLING', 'PAUSED'].includes(well.status)) return 'Core while drilling.';
  return null;
}

export function coreWellImpl(state, wellId) {
  const well = state.wells[wellId];
  const why = canCore(state, well);
  if (why) return { ok: false, msg: why };
  const price = unitPrice(state, eqDef('rotary_coring'));
  if (!spend(state, price, 'consumables')) return { ok: false, msg: `A core run costs ${fmt$(price)}.` };
  well.cost = (well.cost ?? 0) + price;
  well.cored ??= [];
  const top = well.depth, bottom = well.depth + CORE_FT;
  well.cored.push([Math.round(top), Math.round(bottom)]);
  checkCores(state, well);
  return { ok: true, msg: `Core barrel on: the next ${CORE_FT} ft of ${well.name} (${Math.round(top)}–${Math.round(bottom)} ft) come up as solid rock.` };
}

// Daily: any bypassed reservoir inside a cored interval that the bit has passed is revealed.
export function checkCores(state, well) {
  if (!well.cored?.length || !well.bypassed?.length) return;
  const fdef = fieldDef(well.fieldId);
  const plot = state.fields[well.fieldId]?.plots[well.plot];
  if (!plot) return;
  const ids = [];
  for (const r of reservoirsAt(fdef, plot)) {
    if (!well.bypassed.includes(r.id) || well.depth <= r.top) continue;
    const b = r.top + (r.thick ?? 15);
    if (well.cored.some(([t, bt]) => t < b && bt > r.top)) ids.push(r.id);
  }
  if (!ids.length) return;
  const found = revealReservoirs(state, well, ids, 'core');
  if (found.length) notify(state, 'news', { kind: 'survey', wellId: well.id, fieldId: well.fieldId, level: 'major', text: `Oil-stained sand in the core from ${well.name}! The mud hid a pay zone — recomplete up-hole.` });
}

export function tickCores(state) {
  for (const w of Object.values(state.wells)) if (w.cored?.length && w.bypassed?.length) checkCores(state, w);
}

// ---- views ---------------------------------------------------------------------------------
export function surveyView(state, fieldId) {
  const f = state.fields[fieldId];
  const fdef = fieldDef(fieldId);
  if (!f || !fdef) return null;
  const list = [];
  for (const id of ['creekology', 'dowsing', 'geologist', 'torsion_balance', 'seismic_refraction', 'seismic_2d', 'seismic_3d', 'seismic_4d_ai']) {
    const def = eqDef(id);
    if (!def) continue;
    const available = isAvailable(def, state);
    const done = (f.surveysDone ?? []).includes(id) && !stat(def, 'random', false);
    list.push({ id, name: def.name, year: def.year, price: unitPrice(state, def) * surveyPriceMult(state, def), days: stat(def, 'days', 1),
      sigma: surveySigma(state, def, fdef) + (id === 'geologist' ? geologistSigmaDelta(state) : 0), depthErr: stat(def, 'depthErr', null), dome: stat(def, 'domeSigma', null) != null && isDomeField(fdef),
      available, done, needsRole: stat(def, 'needsRole', null), reason: !available ? `Available ${def.year}` : done ? 'Already surveyed' : null });
  }
  const showPressure = hasUpgrade(state, 'bhp_bomb');
  return {
    fieldId, dome: isDomeField(fdef), geophysicist: hasGeophysicist(state), methods: list, priceMultGeophysics: hasGeophysicist(state) ? 0.5 : 1,
    plots: f.plots.map((p, idx) => ({ idx, est: p.survey?.est ?? null, sigma: p.survey?.sigma ?? null, methods: p.survey?.methods ?? [], payDepth: p.survey?.payDepth ?? null })),
    showPressure,
    reservoirs: showPressure ? Object.entries(f.reservoirs).filter(([, r]) => r.remaining < r.initial).map(([id, r]) => ({ id, pressure: r.pressure, remainingPct: r.initial > 0 ? r.remaining / r.initial : 0 })) : null,
    poreLog: f.poreLog ?? [],
  };
}

export function wellLogView(state, wellId) {
  const w = state.wells[wellId];
  if (!w) return null;
  return {
    eLogged: !!w.eLogged, canLog: !canLog(state, w), logReason: canLog(state, w), logCost: unitPrice(state, eqDef('e_log_run')),
    zones: w.eLog?.zones ?? [], logDay: w.eLog?.day ?? null, bypassed: w.bypassed ?? [], bypassedFound: w.bypassedFound ?? [],
    cored: w.cored ?? [], canCore: !canCore(state, w), coreReason: canCore(state, w), coreCost: unitPrice(state, eqDef('rotary_coring')), coreFt: CORE_FT,
    knownPore: w.knownPore ?? null,
  };
}
